import React, { useCallback, useState, useRef, useMemo } from 'react'
import { useLingui } from '@lingui/react/macro'

import { useFormikContext } from 'formik'

import { withStaticProperties, YStack, XStack, StackProps } from 'tamagui'

import { Button, ButtonProps } from '../Button/Button'
import { Dialog } from '../Dialog/Dialog'

type ConfirmationConfig = {
  title: string
  description: string
  warning?: string
  confirmLabel?: string
  cancelLabel?: string
}

export const FormSubmitButton = withStaticProperties(
  ({
    children,
    alignItems = 'flex-end',
    secondary,
    buttonProps = {},
    confirmation,
    ...props
  }: StackProps & {
    secondary?: boolean
    buttonProps?: ButtonProps
    confirmation?: ConfirmationConfig
  }) => {
    const { t } = useLingui()
    const formContextRef = useRef(useFormikContext())

    formContextRef.current = useFormikContext()

    const [forcedState, setForcedState] = useState<
      'notPressed' | 'pressed' | 'success' | 'error'
    >('notPressed')

    const [showConfirmation, setShowConfirmation] = useState(false)

    const doSubmit = useCallback(async () => {
      let submitError = false
      try {
        setForcedState('pressed')

        await formContextRef.current.submitForm()
      } catch {
        submitError = true
      } finally {
        if (
          submitError ||
          Object.keys(formContextRef.current.errors).length ||
          formContextRef.current.status
        ) {
          setForcedState('error')
        } else {
          setForcedState('success')
        }

        await new Promise<void>((resolve) => {
          setTimeout(() => {
            resolve()
          }, 2000)
        })

        setForcedState('notPressed')
      }
    }, [setForcedState])

    const onPress = useCallback(async () => {
      if (confirmation) {
        setShowConfirmation(true)
        return
      }
      await doSubmit()
    }, [confirmation, doSubmit])

    const handleConfirm = useCallback(async () => {
      setShowConfirmation(false)
      await doSubmit()
    }, [doSubmit])

    // Wrap string children in Button.Text for proper styling
    const wrappedChildren = useMemo(() => {
      if (typeof children === 'string') {
        return <Button.Text>{children}</Button.Text>
      }
      return children
    }, [children])

    const isSecondary =
      secondary === undefined ? !formContextRef.current.isValid : secondary

    return (
      <YStack {...props} alignItems={alignItems}>
        <Button
          type={confirmation ? 'button' : 'submit'}
          height={38}
          {...buttonProps}
          onPress={onPress}
          secondary={isSecondary}
          forceState={
            formContextRef.current.isSubmitting ? 'pressed' : forcedState
          }
        >
          {wrappedChildren}
        </Button>

        {confirmation &&
          (() => {
            const dialogBody = (
              <>
                <Dialog.Title>{confirmation.title}</Dialog.Title>
                <Dialog.Description size='$5'>
                  {confirmation.description}
                </Dialog.Description>
                {confirmation.warning && (
                  <Dialog.Description size='$5' color='$colorMuted'>
                    {confirmation.warning}
                  </Dialog.Description>
                )}

                <XStack gap='$3' justifyContent='flex-end'>
                  <Dialog.Close asChild>
                    <Button secondary height={38}>
                      <Button.Text>
                        {confirmation.cancelLabel ?? t`Cancel`}
                      </Button.Text>
                    </Button>
                  </Dialog.Close>

                  <Button type='button' height={38} onPress={handleConfirm}>
                    <Button.Text>
                      {confirmation.confirmLabel ?? t`Confirm`}
                    </Button.Text>
                  </Button>
                </XStack>
              </>
            )

            return (
              <Dialog
                open={showConfirmation}
                onOpenChange={setShowConfirmation}
              >
                <Dialog.Portal>
                  <Dialog.Content padding='$5' gap='$3'>
                    {dialogBody}
                  </Dialog.Content>
                </Dialog.Portal>

              </Dialog>
            )
          })()}
      </YStack>
    )
  },
  { Text: Button.Text }
)
